import { useContext } from "react";
import { AuthContext } from "common/AuthProvider";

// Permission names for the Finance area.
//
// This is the exact string stored in dbo.Permission. AuthProvider compares it with a
// case-sensitive ===, so the casing here has to match the seed migration
// (20260918_AALLAN_01_AddBnsfFollowUpsPermission.sql) character for character.
export const FinancePermission = {
  /**
   * Run the BNSF Follow-Ups workbook through the classifier and download the filled response.
   *
   * Permission-only gate (no role union) on both the menu entry (routes.tsx) and the
   * /finance/bnsf-followups route (MainLayout.tsx), same reasoning as AccessShifts: a role
   * union would let anyone holding a listed role reach the page after the permission is
   * revoked, which would make revoking it do nothing.
   *
   * Seeded 2026-09-18 to Admin and FinanceAdmin.
   */
  AccessBnsfFollowUps: "AccessBnsfFollowUps",

  /**
   * The Finance area permission. Already seeded and already in use -- it gates the Finance menu
   * (routes.tsx) and the Shop Invoice Submission card (FormsList.tsx).
   *
   * BNSF Road Disputes rides this rather than a permission of its own: a new permission needs a
   * seed migration before anyone holds it, which left the screen invisible to everybody. Named
   * as a constant here because AuthProvider compares permission names with a case-sensitive
   * ===, so the two loose string literals that predate it are a typo waiting to happen.
   */
  AccessFinance: "AccessFinance",
} as const;

export interface FinancePermissions {
  canAccessBnsfFollowUps: boolean;
  canAccessFinance: boolean;
}

/** What the signed-in user may reach in the Finance area beyond the AccessFinance area gate. */
export const useFinancePermissions = (): FinancePermissions => {
  const { userHasPermission } = useContext(AuthContext);

  return {
    canAccessBnsfFollowUps: userHasPermission(FinancePermission.AccessBnsfFollowUps),
    canAccessFinance: userHasPermission(FinancePermission.AccessFinance),
  };
};
